import React from 'react';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import { ToggleButtonGroup as MUIToggleButtonGroup } from '@mui/material';
import Button from 'components/control/button';
import { testIds } from 'constants/test-constants';
import { sanitizeTestid } from 'utils/stringSanitization';
import { SEARCH_OPTION_AGENTS, SEARCH_OPTION_BUY, SEARCH_OPTION_RENT, SEARCH_OPTION_SOLD, SEARCH_OPTION_HOME_APPRAISAL } from '@tenants/types/theme';
import { trackEvent } from 'utils/google-tag-manager';
import {
  GTM_CLICK_HOMEPAGE_SEARCH_OPTION_AGENTS,
  GTM_CLICK_HOMEPAGE_SEARCH_OPTION_BUY,
  GTM_CLICK_HOMEPAGE_SEARCH_OPTION_RENT,
  GTM_CLICK_HOMEPAGE_SEARCH_OPTION_SOLD,
} from 'constants/events';
import { useTranslation } from 'next-i18next';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';

import type { SelectOptionValue, SelectOption } from '@zoocasa/node-kit/components/controls/types';

interface Props<T extends SelectOptionValue> {
  options: SelectOption<T>[];
  value: T;
  onValueChange: (option: T) => void;
  isExpTheme: boolean;
}

export default function SearchOptionButtonGroup<T extends SelectOptionValue>({ options, value, onValueChange, isExpTheme = false }: Props<T>) {
  const { t } = useTranslation(Namespaces.SEARCH);

  const getTranslatedLabel = (optionValue: SelectOptionValue): string => {
    switch (optionValue) {
      case SEARCH_OPTION_BUY:
        return t(LOCALE_SEARCH_PATHS.BAR_OPTION_BUY, { defaultValue: 'Buy' });
      case SEARCH_OPTION_RENT:
        return t(LOCALE_SEARCH_PATHS.BAR_OPTION_RENT, { defaultValue: 'Rent' });
      case SEARCH_OPTION_SOLD:
        return t(LOCALE_SEARCH_PATHS.BAR_OPTION_SOLD, { defaultValue: 'Sold' });
      case SEARCH_OPTION_AGENTS:
        return t(LOCALE_SEARCH_PATHS.BAR_OPTION_AGENTS, { defaultValue: 'Agents' });
      case SEARCH_OPTION_HOME_APPRAISAL:
        return t(LOCALE_SEARCH_PATHS.BAR_OPTION_HOME_APPRAISAL, { defaultValue: 'Home Appraisal' });
      default:
        // Fallback to original label for any unhandled options
        return options.find(o => o.value === optionValue)?.label || String(optionValue);
    }
  };

  const handleTrackEvents = (option: SelectOptionValue) => {
    if (option === SEARCH_OPTION_BUY) {
      trackEvent(GTM_CLICK_HOMEPAGE_SEARCH_OPTION_BUY);
    } else if (option === SEARCH_OPTION_RENT) {
      trackEvent(GTM_CLICK_HOMEPAGE_SEARCH_OPTION_RENT);
    } else if (option === SEARCH_OPTION_SOLD) {
      trackEvent(GTM_CLICK_HOMEPAGE_SEARCH_OPTION_SOLD);
    } else if (option === SEARCH_OPTION_AGENTS) {
      trackEvent(GTM_CLICK_HOMEPAGE_SEARCH_OPTION_AGENTS);
    }
  };

  return (
    <div className={styles.component}>
      <MUIToggleButtonGroup
        aria-label="button group"
        className={buildClassName(styles['button-group'], isExpTheme && styles['exp-theme'])}
      >
        {options.map(({ value: option }, index) => {
          const translatedLabel = getTranslatedLabel(option);
          return (
            <Button
              key={index}
              className={buildClassName(styles['button-style'], option === value && styles['button-style-selected'])}
              onClick={() => {
                handleTrackEvents(option);
                onValueChange(option);
              }}
              type="button"
              label={translatedLabel}
              testId={sanitizeTestid(translatedLabel) + testIds.searchOptionButton}
            />
          );
        })}
      </MUIToggleButtonGroup>
    </div>
  );
}
